import React, { createContext, useCallback, useContext, useState } from 'react';

import { PaymentError } from '../core/payment-error';
import { CustomerFields } from '../types/config';

interface CustomerDataContextValue {
    customerData: CustomerFields | null;
    updateCustomerFields: (fields: CustomerFields) => void;
}

const CustomerDataContext = createContext<CustomerDataContextValue | undefined>(
    undefined
);

interface CustomerDataProviderProps {
    children: React.ReactNode;
    initialData?: CustomerFields;
}

/**
 * Provider component for managing customer fields across the payment widget.
 * Enables prefilling payment forms and dynamic updates without re-initialization.
 */
export const CustomerDataProvider: React.FC<CustomerDataProviderProps> = ({
    children,
    initialData = null
}) => {
    const [customerData, setCustomerData] = useState<CustomerFields | null>(
        initialData
    );

    const updateCustomerFields = useCallback((fields: CustomerFields) => {
        setCustomerData(fields);
    }, []);

    return (
        <CustomerDataContext.Provider
            value={{ customerData, updateCustomerFields }}
        >
            {children}
        </CustomerDataContext.Provider>
    );
};

/**
 * Hook to access customer data context.
 * Must be used within a CustomerDataProvider.
 */
export const useCustomerData = (): CustomerDataContextValue => {
    const context = useContext(CustomerDataContext);
    if (!context) {
        throw new PaymentError(
            'useCustomerData must be used within CustomerDataProvider',
            'VALIDATION_ERROR'
        );
    }
    return context;
};
